import Vue from 'vue'
import axios from 'axios'
import Constants from 'src/utilities/constants'
import {
  camelize,
  formatApiErrors,
  sortBySort,
  objectById,
  objectBy,
  kvpsActiveInactive,
  setLocalStorage,
  checkLocalStorage
} from 'src/utilities/utils'
import _ from 'lodash'
import {make} from 'vuex-pathify'
import {crudifyMutations, resetState, hydrateEdit, savePayload} from 'src/utilities/vuex2'
import {ptracGetEntities} from 'src/utilities/ptracApi'
import {OK} from 'http-status-codes'
import {addHeaders} from 'src/utilities/authHeader'

let initialState = {
  results: [],
  optionValue: {
    label: null,
    value: null
  },
  edit: {
    id: null,
    name: null,
    key: null,
    departments: [],
    active: true,
    fields: []
  },
  fieldsInUse: {},
  active: { fields: [] }
}

const state = Vue.util.extend({}, initialState)
const getters = make.getters(state)

getters.kvpsForSelect = (state) => {
  return kvpsActiveInactive(state.results)
}
getters.editFields = (state) => {
  return state.edit.fields
}
getters.editSortedFields = (state) => {
  if (!state.edit.fields) {
    return []
  }
  return [...state.edit.fields].sort((a, b) => {
    if (a.sort > b.sort) { return 1 }
    if (a.sort < b.sort) { return -1 }
    return 0
  })
}
getters.fieldInUse = (state) => (key) => {
    return key && state.fieldsInUse[key] > 0
}

getters.activeTemplates = (state) => {
  const activeTaskTemplates = []
  state.results.forEach((taskTemplate) => {
    if (taskTemplate.active) {
      activeTaskTemplates.push({
        value: taskTemplate.id,
        label: taskTemplate.name
      })
    }
  })

  return activeTaskTemplates
}

getters.savePayload = savePayload

const mutations = make.mutations(state)
mutations.reset = resetState
mutations.hydrateEdit = hydrateEdit
crudifyMutations(mutations)

mutations.generateKey = (state) => {
  state.edit.key = camelize(state.edit.name)
}

mutations.activeTaskTemplate = (state, payload) => {
  state.active = payload
  state.active.fields = sortBySort(payload.fields)
}

mutations.resetEdit = (state) => {
  state.edit = {
    id: null,
    name: null,
    key: null,
    departments: [],
    active: true,
    fields: []
  }
}

mutations.storeField = (state, field) => {
  let existingField = objectBy(state.edit.fields, 'key', field.key)
  if (existingField) {
    Object.assign(existingField, field)
  } else {
    if (!state.edit.fields) {
      state.edit.fields = []
    }
    state.edit.fields.push(field)
  }
}

mutations.editFieldsRemove = (state, row) => {
  state.edit.fields = _.filter(state.edit.fields, (field) => field.key !== row.key)
}

mutations.editFieldsSort = (state, payload) => {
  state.edit.fields.forEach((field) => {
    field.sort = payload[field.key]
  })
}

mutations.editFieldsValuesSort = (state, payload) => {
  const field = objectBy(state.edit.fields, 'key', payload.fieldKey)
  field.values.forEach((optionValue) => {
    optionValue.sort = payload.optionValues[optionValue.value]
  })
}

const loadAllTemplates = async (state, ignoreLocalStorage) => {
  const response = await ptracGetEntities('task-templates/findAllIncludingInactive')
  if (OK === response.status) {
    state.results = response.data
    setLocalStorage(Constants.LOCAL_STORAGE.LOCATION.TASK_TEMPLATES, state.results)
  }
}

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions: {
    async fetchFieldsInUse({ state }, taskTemplateId) {
      let baseUrl = process.env.VUE_APP_PTRAC_API_URI + '/tasks/search/countByTaskTemplateIdAndFieldValueKey?templateId=' + taskTemplateId + '&key='
      const taskTemplate = objectById(state.results, taskTemplateId)
      if (taskTemplate.fields) {
        for (let field of taskTemplate.fields) {
          let response = await axios.get(baseUrl + field.key, { headers: await addHeaders() })
          state.fieldsInUse[field.key] = response.data
        }
      }
    },
    async fetchAll({ state }, ignoreLocalStorage) {
      if (ignoreLocalStorage) {
        await loadAllTemplates(state, ignoreLocalStorage)
        return
      }

      if (checkLocalStorage(state, Constants.LOCAL_STORAGE.LOCATION.TASK_TEMPLATES, Constants.LOCAL_STORAGE.EXPIRATION.ONE_DAY)) {
        return
      }

      await loadAllTemplates(state, ignoreLocalStorage)
    },
    // TODO refactor and delete - use delete in store.js
    async deleteById({ commit, state }, id) {
      let url = process.env.VUE_APP_PTRAC_API_URI + '/task-templates/' + id
      try {
        await axios.delete(url, { headers: await addHeaders() })
        commit('delete', id)
        commit('resetEdit')
        this.$eventHub.$emit('taskTemplateReset') // TODO - get rid of this
        return {
          result: Constants.RETURN_CODES.SUCCESS
        }
      } catch (err) {
        return {
          result: Constants.RETURN_CODES.ERROR,
          message: formatApiErrors(err.response.data.errors)
        }
      }
    },
    sort({ commit, state }, sortedTaskTypes) { // TODO - refactor - use async await
      const self = this
      sortedTaskTypes.forEach(async function (taskType) {
        const patch = [
          { op: 'replace', path: '/sort', value: taskType.sort }
        ]
        axios.patch(taskType.link, patch, { headers: await addHeaders(Constants.HEADERS.PATCH) })
          .then(response => {
            commit('sort', taskType)
          })
          .catch(error => {
            // TODO - add rollbar logging
            const payload = {
              msg: 'Failed to Sort Task Template',
              type: 'danger'
            }
            self.$eventHub.$emit('notifyVue', payload)
            console.error(error)
          })
      })
    }
  }
}
